import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNotification } from "../../../../hooks/useNotification";
import { usePagination } from "../../../../hooks/usePagination";
import api from "../../../../util/api";
import { getApiErrorMessage } from "../../../../util/apiErrorMessage";
import {
  PaginatedResponse,
  QueryKeys,
  UserApiType,
} from "../../../../util/types";

export interface usersFilters {
  from?: string;
  to?: string;
  searchKeyword?: string | null;
  status?: string;
  companyId?: string;
  limit?: number;
}

const ERROR_MESSAGE = "Something went wrong, please try again";
// Stable reference: consumers sync this into state via useEffect, so a new
// [] on every render would loop
const EMPTY_USERS: UserApiType[] = [];

export const useGetusers = (
  filters: usersFilters = {},
  sort = { field: "createdAt", direction: "DESC" },
  status: string | undefined,
  ownerId?: string,
  options: { syncWithUrl?: boolean } = {}
) => {
  const { showNotification } = useNotification();
  const { page, limit, onPaginationChange, resetPageNum } = usePagination({
    syncWithUrl: options.syncWithUrl,
  });
  const { companyId, limit: customLimit, ...rest } = filters;
  const effectiveLimit = customLimit ?? limit;

  // Anything that changes the result set restarts from page 1. Tracked as a
  // string so it can be compared across renders; while a reset is pending the
  // query stays disabled, which avoids a request for the old page followed
  // immediately by one for page 1.
  const filtersKey = JSON.stringify([
    status,
    filters.searchKeyword,
    filters.status,
    filters.from,
    filters.to,
    filters.companyId,
    ownerId,
  ]);
  const appliedFiltersKey = useRef(filtersKey);
  const resetPending = appliedFiltersKey.current !== filtersKey && page !== 1;

  useEffect(() => {
    if (appliedFiltersKey.current === filtersKey) return;
    appliedFiltersKey.current = filtersKey;
    if (page !== 1) resetPageNum();
  }, [filtersKey]);

  const { data, isLoading, isFetching, error, refetch } = useQuery<
    PaginatedResponse<UserApiType>
  >({
    queryKey: [
      QueryKeys.LIST_USERS,
      page,
      effectiveLimit,
      filtersKey,
      sort.field,
      sort.direction,
    ],
    queryFn: () =>
      api.getUsersList(
        page,
        effectiveLimit,
        {
          sort: sort.field,
          sortDirection: sort.direction,
          status,
          ...rest,
        },
        companyId ?? ownerId
      ),
    enabled: !resetPending,
    placeholderData: (prev) => prev,
  });

  useEffect(() => {
    if (error) showNotification(getApiErrorMessage(error, ERROR_MESSAGE));
  }, [error]);

  return {
    loading: isLoading || isFetching,
    refreshusers: () => {
      refetch();
    },
    users: data?.docs ?? EMPTY_USERS,
    totalDocs: data?.totalDocs ?? 0,
    page,
    limit,
    onPaginationChange,
    error: error ? getApiErrorMessage(error, ERROR_MESSAGE) : null,
    ownerId,
  };
};
